// SPÅR 1a — anonymous activity list (V1-baserad ersättning).
//
// Replaces the V2-only `AnonymousOpenMatches.tsx` once Spår 1b raderar
// `public_open_matches_view`. Reads from `public_activities_view` mot
// V1 (`open_matches`) — same security-barrier-vy-mönstret som
// `public_bookings_view` + UPPDRAG-19 PR-C.
//
// Spec D15 — Scenario (a) låst:
//   - Route: `/aktiviteter` (P-A: nya routes)
//   - i18n: använder `activities.*`-namespace i common.json (SPÅR 2a-rebind)
//   - V1-extrafält exponerade (P-B): activity_type_id, match_type,
//     gender_filter, cover_image_url, min_participants
//   - Vyn filtrerar: visibility=public, access_type=public, date>=today,
//     status='open' → varje row är OK för en stranger att se
//
// Click handling:
//   - "Anmäl" → AuthRequiredModal med intent='join_open_match' (Spår 3
//     ersätter med riktig V1-join-flow)
//   - Click row → /aktiviteter/:id
//
// landing_events analytics:
//   - 'anon_activities_view' insertat på mount, fire-and-forget
//
// noindex meta — operationella sidor, ej marknadsföring.

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { usePublicOrgContext } from "@/contexts/PublicOrgContext";
import { useAuthRequired } from "@/contexts/AuthRequiredContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, MapPin, Clock, ChevronRight } from "lucide-react";

interface PublicActivityRow {
  id: string;
  organization_id: string;
  title: string | null;
  description: string | null;
  total_spots: number;
  participant_count: number;
  min_level: number | null;
  max_level: number | null;
  status: string;
  date: string;
  start_time: string;
  end_time: string;
  court_id: string | null;
  activity_type_id: string | null;
  match_type: string | null;
  gender_filter: string | null;
  cover_image_url: string | null;
  min_participants: number | null;
  currency: string;
  created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  open: "bg-green-100 text-green-800 border-green-300",
};

export default function AnonymousActivities() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { org: publicOrg } = usePublicOrgContext();
  const { requireAuth } = useAuthRequired();
  // T2v2/G-053: this is the ANONYMOUS list — authenticated users belong on
  // /play. The redirect return lives AFTER all hooks (rules-of-hooks).
  const { loading: authLoading, isAuthenticated } = useAuth();

  // ── 1. Activities via security-barrier-vyn ──
  // Vyn filtrerar redan på status='open', visibility='public',
  // access_type='public' OR NULL, date>=CURRENT_DATE.
  const { data: activities = [], isLoading } = useQuery({
    queryKey: ["anon-activities", publicOrg?.id],
    queryFn: async () => {
      if (!publicOrg?.id) return [];
      const { data, error } = await supabase
        .from("public_activities_view" as any)
        .select("*")
        .eq("organization_id", publicOrg.id)
        .order("date", { ascending: true })
        .order("start_time", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as PublicActivityRow[];
    },
    enabled: !!publicOrg?.id,
    staleTime: 60_000,
  });

  // ── 2. landing_events analytics ──
  useEffect(() => {
    if (!publicOrg?.id) return;
    supabase
      .from("landing_events" as any)
      .insert({
        event_type: "anon_activities_view",
        hostname: typeof window !== "undefined" ? window.location.hostname : null,
        organization_id: publicOrg.id,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
      } as any)
      .then(() => {
        /* best-effort */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicOrg?.id]);

  const handleJoinClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    requireAuth({
      intent: "join_open_match",
      returnUrl: "/aktiviteter",
    });
  };

  // T2v2/G-053: authenticated users get the logged-in list.
  if (!authLoading && isAuthenticated) return <Navigate to="/play" replace />;

  return (
    <div className="space-y-4 max-w-2xl mx-auto p-4 pb-20 lg:pb-6">
      <div>
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-lg font-semibold">{t("activities.title")}</h1>
          {/* G-071/G-035: cross-link between the two anonymous surfaces */}
          <Link to="/book" className="text-sm font-medium text-primary hover:underline shrink-0">
            {t("nav.bookCourt")} →
          </Link>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {t(
            "activities.anonymous.intro",
            "Bläddra bland öppna matcher. Skapa konto för att anmäla dig.",
          )}
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-28 w-full" />)}
        </div>
      ) : activities.length === 0 ? (
        <div className="text-center py-12 space-y-3">
          <p className="text-muted-foreground">{t("activities.empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activities.map((activity) => {
            const isFull = activity.participant_count >= activity.total_spots;
            return (
              <Link key={activity.id} to={`/aktiviteter/${activity.id}`}>
                <Card className="hover:shadow-md transition-shadow cursor-pointer">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0 space-y-1.5">
                        {/* Title + status */}
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">
                            {activity.title || t("activities.defaultTitle")}
                          </span>
                          <Badge
                            variant="outline"
                            className={`text-[10px] shrink-0 ${STATUS_COLORS[activity.status] ?? ""}`}
                          >
                            {t(`activities.status.${activity.status}`)}
                          </Badge>
                        </div>

                        {/* Time — V1 har alltid specifik date+start_time+end_time */}
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {format(parseISO(`${activity.date}T${activity.start_time}`), "EEE d MMM HH:mm", { locale: sv })}
                          </span>
                          {activity.court_id && (
                            <span className="flex items-center gap-1 opacity-50">
                              <MapPin className="h-3 w-3" />
                            </span>
                          )}
                        </div>

                        {/* Participant count (aggregate only) */}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Users className="h-3 w-3" />
                          <span>{activity.participant_count}/{activity.total_spots}</span>
                        </div>

                        {/* Level range */}
                        {(activity.min_level || activity.max_level) && (
                          <div className="text-xs text-muted-foreground">
                            {t("activities.level")}: {activity.min_level ?? "—"}–{activity.max_level ?? "—"}
                          </div>
                        )}
                      </div>

                      {/* Action button */}
                      <div className="shrink-0 flex items-center gap-2">
                        {isFull ? (
                          <Badge variant="secondary" className="text-xs">{t("activities.full")}</Badge>
                        ) : (
                          <Button size="sm" onClick={handleJoinClick}>
                            {t("activities.join")}
                          </Button>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      {/* Bottom CTA */}
      <div className="flex justify-center pt-4">
        <button
          type="button"
          onClick={() => navigate("/auth?mode=signup&returnUrl=/aktiviteter")}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-6 py-2 text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          {t("activities.anonymous.signupCta", "Skapa konto för att gå med")}
        </button>
      </div>

      <NoIndexMeta />
    </div>
  );
}

function NoIndexMeta() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);
  return null;
}
